import React from "react";
import { Routes, Route, Navigate, useLocation, useNavigate } from "react-router-dom";
import { RouteOptions } from "../types/route-options";
import { ToastContainer } from "react-toastify";
import { isLocalAuthenticated, ensureAuthOrLogout } from "@services/auth";
import { FEATURE_NAMES } from "@utils/permissions";
import PermissionGuard from "@components/PermissionGuard/PermissionGuard";
import AccessRemoved from "@screens/AccessRemoved/AccessRemoved";
import {
  hasRecentFacilityAccessAllowed,
  resolveFacilityAccessBlocked,
} from "@services/facilityManagement";
import { FACILITY_ACCESS_REMOVED_EVENT } from "@utils/authenticatedFetch";
import "react-toastify/dist/ReactToastify.css";
import "../styles/toast.css";
import { UniComply } from "@screens/UniComply/Index";
import AviationLanding from "@screens/AviationLanding/AviationLanding";
import Login from "@screens/Login/Login";
import SignUp from "@screens/Signup/Signup";
import ForgotPassword from "@screens/ForgotPassword/ForgotPassword";
import VerifyOTP from "@screens/VerifyOTP/Index";
import EmailVerification from "@screens/EmailVerification/Index";
import ResetPassword from "@screens/ResetPassword/Index";
import ConfirmInvitation from "@screens/ConfirmInvitation/Index";
import ResetPasswordSuccess from "@screens/ResetPasswordSuccess/Index";
import { Dashboard } from "@screens/Dashboard/Dashboard";
import { FacilityLayoutDataProvider } from "@context";
import SpacesPage from "@components/Spaces/Spaces";
import Discovery from "@components/Discovery/Discovery";
import Pricing from "@screens/Pricing/Pricing";
import PaymentSuccess from "@screens/PaymentSuccess/Index";
import PaymentFailure from "@screens/PaymentFailure/Index";
import PaymentMethodUpdateSuccess from "@screens/PaymentMethodUpdateSuccess/Index";
import PaymentMethodUpdateFailure from "@screens/PaymentMethodUpdateFailure/Index";
import FacilityCompliance from "@components/Spaces/FacilityCompliance";
import FacilityComplianceStreaming from "@components/Spaces/FacilityComplianceStreaming";
import ContactUs from "@screens/ContactUs/ContactUs";
import LandingPage from "@components/Spaces/LandingPage";
import ArticleDetail from "@components/Discovery/ArticleDetail";
import DiscoverDetails from "@screens/DiscoverDetails/DiscoverDetails";
import DiscoverChat from "@screens/DiscoverChat/DiscoverChat";
import TermsOfService from "@screens/TermsOfService/TermsOfService";
import PrivacyPolicy from "@screens/PrivacyPolicy/PrivacyPolicy";
import BAAPolicy from "@screens/BAAPolicy/BAAPolicy";
import ChangePassword from "@screens/ChangePassword/Index";
import UserManagement from "@screens/Settings/Index";
import BillingPage from "@screens/Billing";
import ProfilePage from "@screens/ProfilePage/Index";
import AuditLogsPage from "@screens/AuditLogs/Index";
import NotificationsPage from "@screens/Notifications";
import MultiFacilityStatusPage from "@screens/MultiFacilityStatus/MultiFacilityStatusPage";
import { canAccessMultiFacilityStatusPage } from "@utils/userUtils";
import SurveyViewer from "@screens/SurveyViewer/Index";
import AgreementsPage from "@screens/Agreements/Agreements";
import AgreementsAcceptancePage from "@screens/Agreements/AgreementsAcceptance";
import TrainingVideosPage from "@screens/TrainingVideos/Index";
import SignupInputForm from "../screens/SignupInputForm/Index";

const ACCESS_REMOVED_PATH = "/access-removed";

/** Unauthenticated dashboard access → login with `return_to` (same pattern as Pricing query params). */
function AuthGuard({ children }: { children: React.ReactNode }): JSX.Element {
  const location = useLocation();
  if (ensureAuthOrLogout()) {
    return <>{children}</>;
  }
  const intended = `${location.pathname}${location.search}`;
  const loginParams = new URLSearchParams();
  if (intended.startsWith("/dashboard")) {
    loginParams.set("return_to", intended);
  }
  const loginPath = loginParams.toString()
    ? `/login?${loginParams.toString()}`
    : "/login";
  return <Navigate to={loginPath} replace />;
}

/** Keep backend-generated billing links compatible with SPA routing. */
function LegacyBillingRedirect(): JSX.Element {
  const location = useLocation();
  return (
    <Navigate
      to={`/dashboard/billing${location.search}${location.hash}`}
      replace
    />
  );
}

function LoginRedirectWhenAuthenticated(): JSX.Element {
  const location = useLocation();
  const [target, setTarget] = React.useState<string | null>(() => {
    const returnTo = new URLSearchParams(location.search).get("return_to");
    if (
      hasRecentFacilityAccessAllowed() &&
      returnTo &&
      returnTo.startsWith("/dashboard") &&
      !returnTo.includes("..")
    ) {
      return returnTo;
    }
    return hasRecentFacilityAccessAllowed() ? "/dashboard" : null;
  });

  React.useEffect(() => {
    let cancelled = false;
    void (async () => {
      const blocked = await resolveFacilityAccessBlocked();
      if (cancelled) return;
      if (blocked) {
        setTarget(ACCESS_REMOVED_PATH);
        return;
      }
      const returnTo = new URLSearchParams(location.search).get("return_to");
      if (
        returnTo &&
        returnTo.startsWith("/dashboard") &&
        !returnTo.includes("..")
      ) {
        setTarget(returnTo);
        return;
      }
      setTarget("/dashboard");
    })();
    return () => {
      cancelled = true;
    };
  }, [location.search]);

  if (!target) return <></>;
  return <Navigate to={target} replace />;
}

function FacilityAccessGuard({ children }: { children: React.ReactNode }): JSX.Element {
  const location = useLocation();
  const navigate = useNavigate();
  const [allowed, setAllowed] = React.useState(
    location.pathname === ACCESS_REMOVED_PATH || hasRecentFacilityAccessAllowed(),
  );

  React.useEffect(() => {
    const redirect = () => {
      if (location.pathname !== ACCESS_REMOVED_PATH) {
        navigate(ACCESS_REMOVED_PATH, { replace: true });
      }
    };
    window.addEventListener(FACILITY_ACCESS_REMOVED_EVENT, redirect);
    return () => window.removeEventListener(FACILITY_ACCESS_REMOVED_EVENT, redirect);
  }, [location.pathname, navigate]);

  React.useEffect(() => {
    if (location.pathname === ACCESS_REMOVED_PATH) {
      setAllowed(true);
      return;
    }
    if (hasRecentFacilityAccessAllowed()) {
      setAllowed(true);
      return;
    }
    setAllowed(false);
    let cancelled = false;
    void (async () => {
      const blocked = await resolveFacilityAccessBlocked();
      if (cancelled) return;
      if (blocked) {
        navigate(ACCESS_REMOVED_PATH, { replace: true });
        return;
      }
      setAllowed(true);
    })();
    return () => {
      cancelled = true;
    };
  }, [location.pathname, navigate]);

  if (!allowed) return <></>;
  return <>{children}</>;
}

function AccessRemovedRoute(): JSX.Element {
  const [blocked, setBlocked] = React.useState<boolean | null>(() =>
    hasRecentFacilityAccessAllowed() ? false : null,
  );

  React.useEffect(() => {
    let cancelled = false;
    void (async () => {
      const result = await resolveFacilityAccessBlocked();
      if (!cancelled) {
        setBlocked(result);
      }
    })();
    return () => {
      cancelled = true;
    };
  }, []);

  if (blocked === null) return <></>;
  if (!blocked) return <Navigate to="/dashboard" replace />;
  return <AccessRemoved />;
}

function MultiFacilityStatusGuardedRoute(): JSX.Element {
  // Global administrators own this surface; do not also require Setup Facility
  // (permission matrix drifts across CI/QA and would redirect admins to /dashboard).
  // Playwright may set __ucForceMultiFacilityStatus for deployed Istanbul coverage.
  //
  // IMPORTANT: never <Navigate to="/dashboard"> on a denied first paint.
  // That drops /facilities/status; Dashboard then sends empty-facility users to
  // facility setup (PW 26323/26333: forceKey=1 after redirect, page never mounts).
  const [allowed, setAllowed] = React.useState(() =>
    canAccessMultiFacilityStatusPage(),
  );

  React.useEffect(() => {
    if (allowed) return;
    let tries = 0;
    const id = window.setInterval(() => {
      tries += 1;
      if (canAccessMultiFacilityStatusPage()) {
        setAllowed(true);
        window.clearInterval(id);
        return;
      }
      if (tries >= 40) {
        window.clearInterval(id);
      }
    }, 100);
    return () => window.clearInterval(id);
  }, [allowed]);

  if (!allowed) {
    return <></>;
  }
  return <MultiFacilityStatusPage />;
}

const routes: RouteOptions[] = [
  {
    id: 1,
    path: "/",
    component: <UniComply />,
  },
  {
    id: 24,
    path: "/healthcare",
    component: <UniComply />,
  },
  {
    id: 25,
    path: "/aviation",
    component: <AviationLanding />,
  },
  {
    id: 2,
    path: "/login",
    component: <Login />,
  },
  {
    id: 3,
    path: "/signup",
    component: <SignUp />,
  },
  {
    id: 4,
    path: "/dashboard/*",
    component: (
      <FacilityLayoutDataProvider>
        <Dashboard />
      </FacilityLayoutDataProvider>
    ),
  },
  {
    id: 5,
    path: "/Pricing",
    component: <Pricing />,
  },
  {
    id: 6,
    path: "/ContactUs",
    component: <ContactUs />,
  },
  {
    id: 7,
    path: "/payment-success",
    component: <PaymentSuccess />,
  },
  {
    id: 8,
    path: "/payment-failure",
    component: <PaymentFailure />,
  },
  {
    id: 23,
    path: "/payment-method-update-success",
    component: <PaymentMethodUpdateSuccess />,
  },
  {
    id: 24,
    path: "/payment-method-update-failure",
    component: <PaymentMethodUpdateFailure />,
  },
  {
    id: 9,
    path: "/terms-of-service",
    component: <TermsOfService />,
  },
  {
    id: 10,
    path: "/privacy-policy",
    component: <PrivacyPolicy />,
  },
  {
    id: 17,
    path: "/hipaa-baa",
    component: <BAAPolicy />,
  },
  {
    id: 11,
    path: "/forgot-password",
    component: <ForgotPassword />,
  },
  {
    id: 12,
    path: "/verify-otp",
    component: <VerifyOTP />,
  },
  {
    id: 19,
    path: "/email-verification",
    component: <EmailVerification />,
  },
  {
    id: 13,
    path: "/reset-password",
    component: <ResetPassword />,
  },
  {
    id: 14,
    path: "/reset-password-success",
    component: <ResetPasswordSuccess />,
  },
  {
    id: 16,
    path: "/confirm-invitation",
    component: <ConfirmInvitation />,
  },
  {
    id: 15,
    path: "/changepassword",
    component: <ChangePassword />,
  },
  {
    id: 18,
    path: "/audit-logs",
    component: <AuditLogsPage />,
  },
  {
    id: 20,
    path: "/signup-request",
    component: <SignupInputForm />,
  },
  {
    id: 21,
    path: "/campaign-60-day-pilot-A",
    component: <SignupInputForm />,
  },
  {
    id: 23,
    path: ACCESS_REMOVED_PATH,
    component: <AccessRemoved />,
  },
  {
    id: 22,
    path: "/Microsoft-Supported-60-Day-Compliance-Pilot",
    component: <SignupInputForm />,
  },
  {
    id: 26,
    path: "/billing",
    component: <LegacyBillingRedirect />,
  },
];

const AppRouter: React.FC = () => {
  return (
    <div>
      {/* <Routes>
        {routes.map((route: RouteOptions) => (
          <Route key={route.id} path={route.path} element={route.component} />
        ))}
        <Route path="*" element={<div>404 Not Found</div>} />
      </Routes> */}
      <Routes>
        {routes.map((route: RouteOptions) => {
          const isMainPublicPath =
            route.path === "/" ||
            route.path === "/healthcare" ||
            route.path === "/aviation" ||
            route.path === "/login" ||
            route.path === "/signup" ||
            route.path === "/signup-request" ||
            route.path === "/campaign-60-day-pilot/:campaignId" ||
            route.path === "/Microsoft-Supported-60-Day-Compliance-Pilot" ||
            route.path === "/forgot-password" ||
            route.path === "/verify-otp" ||
            route.path === "/email-verification" ||
            route.path === "/reset-password" ||
            route.path === "/reset-password-success" ||
            route.path === "/audit-logs";
          const isPaymentPath =
            route.path === "/payment-success" ||
            route.path === "/payment-failure" ||
            route.path === "/payment-method-update-success" ||
            route.path === "/payment-method-update-failure";
          const isDashboard = route.path.startsWith("/dashboard");
          const isAccessRemovedPath = route.path === ACCESS_REMOVED_PATH;

          let element;
          if (isAccessRemovedPath) {
            element = isLocalAuthenticated() ? (
              <AccessRemovedRoute />
            ) : (
              <Navigate to="/login" replace />
            );
          } else if (isMainPublicPath) {
            if (
              route.path === "/reset-password-success" ||
              route.path === "/email-verification"
            ) {
              // ✅ Always allow showing success page and email verification page
              // For email verification, check if user is already verified
              if (route.path === "/email-verification" && isLocalAuthenticated()) {
                try {
                  const userStr = localStorage.getItem("user");
                  if (userStr) {
                    const user = JSON.parse(userStr);
                    // If email is already verified, redirect to dashboard
                    if (user.is_verified === true) {
                      element = <Navigate to="/dashboard" replace />;
                    } else {
                      element = route.component;
                    }
                  } else {
                    element = route.component;
                  }
                } catch {
                  element = route.component;
                }
              } else {
                element = route.component;
              }
            } else {
              // 🔒 Keep auth check for other public pages
              element = isLocalAuthenticated() ? (
                route.path === "/login" ? (
                  <LoginRedirectWhenAuthenticated />
                ) : (
                  <Navigate to="/dashboard" replace />
                )
              ) : (
                route.component
              );
            }
          } else if (isDashboard || isPaymentPath) {
            element = (
              <AuthGuard>
                <FacilityAccessGuard>{route.component}</FacilityAccessGuard>
              </AuthGuard>
            );
          } else {
            element = route.component;
          }

          return (
            <Route key={route.id} path={route.path} element={element}>
              {isDashboard && (
                <>
                  {/* Default → redirect to facilityValidation */}
                  {/* <Route
                    index
                    element={
                      <Navigate
                        to="spaces"
                        replace
                        state={{ currentView: "facilityValidation" }}
                      />
                    }
                  /> */}
                  <Route
                    index
                    element={(() => {
                      const userStr = localStorage.getItem("user");
                      if (userStr) {
                        try {
                          const user = JSON.parse(userStr);

                          if (user.user_preferred_plan?.plan_name === "Basic") {
                            return (
                              // <Navigate
                              //   to="home"
                              //   replace
                              //   state={{ showMyFacilityDialog: true }}
                              // />
                              <Navigate
                                to="facilities"
                                replace
                                state={{ currentView: "MyFacility" }}
                              />
                            );
                          } else {
                            return (
                              <Navigate
                                to="facilities"
                                replace
                                state={{ currentView: "MyFacility" }}
                              />
                            );
                          }
                        } catch (error) {
                          return <Navigate to="home" replace />;
                        }
                      }
                      return <Navigate to="home" replace />;
                    })()}
                  />
                  <Route path="home" element={<LandingPage />} />
                  <Route
                    path="discover"
                    element={
                      <PermissionGuard featureName={FEATURE_NAMES.DISCOVER} action="view">
                        <Discovery />
                      </PermissionGuard>
                    }
                  />
                  <Route
                    path="discover/article/:id"
                    element={
                      <PermissionGuard featureName={FEATURE_NAMES.DISCOVER} action="view">
                        <ArticleDetail />
                      </PermissionGuard>
                    }
                  />
                  <Route
                    path="discover/details/:linkId"
                    element={
                      <PermissionGuard featureName={FEATURE_NAMES.DISCOVER} action="view">
                        <DiscoverDetails />
                      </PermissionGuard>
                    }
                  />
                  <Route
                    path="discover/chat"
                    element={
                      <PermissionGuard featureName={FEATURE_NAMES.DISCOVER} action="view">
                        <DiscoverChat />
                      </PermissionGuard>
                    }
                  />
                  <Route
                    path="facilities/status"
                    element={<MultiFacilityStatusGuardedRoute />}
                  />
                  <Route path="facilities" element={<SpacesPage />} />
                  <Route path="network" element={<SpacesPage />} />
                  <Route path="network/overview" element={<Navigate to="/dashboard/network" replace />} />
                  <Route path="network/gate" element={<SpacesPage />} />
                  <Route path="network/intel" element={<SpacesPage />} />
                  <Route path="network/intel/upload" element={<SpacesPage />} />
                  <Route path="network/intel/review" element={<SpacesPage />} />
                  <Route path="network/intel/gate" element={<SpacesPage />} />
                  <Route path="intel" element={<Navigate to="/dashboard/network/intel" replace />} />
                  <Route path="intel/upload" element={<Navigate to="/dashboard/network/intel/upload" replace />} />
                  <Route path="intel/review" element={<Navigate to="/dashboard/network/intel/review" replace />} />
                  <Route path="intel/gate" element={<Navigate to="/dashboard/network/intel/gate" replace />} />
                  <Route path="MyFacility" element={<SpacesPage />} />
                  <Route
                    path="MyFacility/compliance"
                    element={<FacilityCompliance />}
                  />
                  <Route
                    path="facilities/compliance"
                    element={<FacilityCompliance />}
                  />
                  <Route
                    path="facilities/compliance-streaming"
                    element={<FacilityComplianceStreaming />}
                  />
                  <Route
                    path="MyFacility/compliance-streaming"
                    element={<FacilityComplianceStreaming />}
                  />
                  <Route path="changepassword" element={<ChangePassword />} />
                  <Route
                    path="usermanagement"
                    element={
                      <PermissionGuard
                        featureName={FEATURE_NAMES.USER_MANAGEMENT}
                        action="view"
                        fallback={<Navigate to="/dashboard" replace />}
                      >
                        <UserManagement />
                      </PermissionGuard>
                    }
                  />
                  <Route path="profile" element={<ProfilePage />} />
                  <Route path="pricing" element={<Pricing />} />
                  <Route path="pricing/order-summary" element={<Pricing />} />
                  <Route path="contact-us" element={<ContactUs />} />
                  <Route
                    path="training-videos"
                    element={
                      <PermissionGuard
                        featureName={FEATURE_NAMES.LEARNING_CENTER}
                        action="view"
                        fallback={<Navigate to="/dashboard" replace />}
                      >
                        <TrainingVideosPage />
                      </PermissionGuard>
                    }
                  />
                  <Route
                    path="agreements"
                    element={
                      <PermissionGuard
                        featureName={FEATURE_NAMES.AGREEMENTS}
                        action="view"
                        fallback={<Navigate to="/dashboard" replace />}
                      >
                        <AgreementsPage />
                      </PermissionGuard>
                    }
                  />
                  <Route
                    path="agreements/acceptance"
                    element={
                      <PermissionGuard
                        featureName={FEATURE_NAMES.AGREEMENTS}
                        action="view"
                        fallback={<Navigate to="/dashboard" replace />}
                      >
                        <AgreementsAcceptancePage />
                      </PermissionGuard>
                    }
                  />
                  <Route path="billing" element={
                      <PermissionGuard
                        featureName={FEATURE_NAMES.BILLINGS}
                        action="view"
                        fallback={<Navigate to="/dashboard" replace />}
                      >
                        <BillingPage />
                      </PermissionGuard>
                    }
                  />
                  <Route path="notifications" element={<NotificationsPage />} />
                  <Route
                    path="audit-logs"
                    element={
                      <PermissionGuard
                        featureName={FEATURE_NAMES.AUDIT_LOGS}
                        action="view"
                        fallback={<Navigate to="/dashboard" replace />}
                      >
                        <AuditLogsPage />
                      </PermissionGuard>
                    }
                  />
                  <Route path="survey-viewer" element={<SurveyViewer />} />
                </>
              )}
            </Route>
          );
        })}
        <Route path="/network" element={<Navigate to="/dashboard/network" replace />} />
        <Route path="/network/overview" element={<Navigate to="/dashboard/network" replace />} />
        <Route path="/network/gate" element={<Navigate to="/dashboard/network/gate" replace />} />
        <Route path="/network/intel" element={<Navigate to="/dashboard/network/intel" replace />} />
        <Route path="/network/intel/upload" element={<Navigate to="/dashboard/network/intel/upload" replace />} />
        <Route path="/network/intel/review" element={<Navigate to="/dashboard/network/intel/review" replace />} />
        <Route path="/network/intel/gate" element={<Navigate to="/dashboard/network/intel/gate" replace />} />
        <Route path="/intel" element={<Navigate to="/dashboard/network/intel" replace />} />
        <Route path="/intel/upload" element={<Navigate to="/dashboard/network/intel/upload" replace />} />
        <Route path="/intel/review" element={<Navigate to="/dashboard/network/intel/review" replace />} />
        <Route path="/intel/gate" element={<Navigate to="/dashboard/network/intel/gate" replace />} />
        <Route path="*" element={<div>404 Not Found</div>} />
      </Routes>
      <ToastContainer
        position="top-right"
        autoClose={4000}
        hideProgressBar={true}
        newestOnTop={false}
        closeOnClick
        rtl={false}
        pauseOnFocusLoss
        draggable
        pauseOnHover
        theme="light"
        closeButton={false}
        icon={false}
        toastClassName="figma-toast"
        bodyClassName="figma-toast-body"
        style={{
          fontFamily: '"Rethink Sans", sans-serif',
        }}
      />
    </div>
  );
};

export { AppRouter };
